import React from 'react';

import { useSelector } from 'hooks';
import usePreAuthorizedAmounts from 'hooks/usePreAuthorizedAmounts';
import { arePreApprovedPricesHidden, getCountry, getValueAddedTax } from 'modules/selfServices/selectors';
import { computeBasketTotals } from 'utils/priceUtils';

import type { Extra } from 'types/ExtrasForm';
import type { ProactiveWork } from 'types/RepairOrderForm';
import type { RepairOrder } from 'modules/selfServices/types/SelfService';

type UseBasketTotalsParams = {
  extras: Extra[];
  proactiveWorks: ProactiveWork[];
  repairOrderItems: RepairOrder[];
};

const useBasketTotals = ({ extras, proactiveWorks, repairOrderItems }: UseBasketTotalsParams) => {
  const vat = useSelector(getValueAddedTax) ?? 0;
  const country = useSelector(getCountry);
  const hidePreApprovedWorksPrices = useSelector(arePreApprovedPricesHidden);
  const { customerPreAuthorizedAmount, hasCustomerPreAuthorizedAmount } = usePreAuthorizedAmounts();

  // Only customer payer is counted in the total
  const customerRepairOrderItems = React.useMemo(
    () => repairOrderItems.filter((repairOrder) => !repairOrder.payerId),
    [repairOrderItems],
  );

  const hiddenRepairOrderPrices = !hasCustomerPreAuthorizedAmount
    && Boolean(hidePreApprovedWorksPrices)
    && customerRepairOrderItems.length > 0;

  // The repair order counts either as its pre-authorized amount, or as its items, unless its prices are hidden
  const countRepairOrderItems = !hasCustomerPreAuthorizedAmount && !hiddenRepairOrderPrices;

  const totals = React.useMemo(() => computeBasketTotals({
    groups: [extras, proactiveWorks, countRepairOrderItems ? customerRepairOrderItems : []],
    vat,
    countryCode: country?.code,
    fixedAmountInclTax: hasCustomerPreAuthorizedAmount ? customerPreAuthorizedAmount : 0,
  }), [extras, proactiveWorks, countRepairOrderItems, customerRepairOrderItems, vat, country?.code, hasCustomerPreAuthorizedAmount, customerPreAuthorizedAmount]);

  return { ...totals, vat, hiddenRepairOrderPrices };
};

export default useBasketTotals;
